import { Check, ArrowRight, Shield, Zap, Coffee } from 'lucide-react';

interface PricingCardProps {
  onStartFree: () => void;
}

export function PricingCard({ onStartFree }: PricingCardProps) {
  const freeFeatures = [
    '3 AI coaching sessions per week',
    '10 journal entries per month',
    'Daily progress tracking',
    'Private and anonymous',
  ];

  const premiumFeatures = [
    'Unlimited AI coaching sessions',
    'Unlimited journal entries',
    'Weekly progress reports',
    'Personalized action plans',
    'Priority support',
    'Cancel anytime — no hassle',
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-white to-[#FDF8F4]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
            Start free. Upgrade when ready.
          </h2>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-full text-amber-800 text-sm font-medium">
            <Coffee className="h-4 w-4" />
            <span>Premium is less than a coffee a week — $9/month, cancel anytime</span>
          </div>
        </div>

        <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-6 items-start">
          {/* Free Plan */}
          <div className="bg-white rounded-3xl shadow-lg overflow-hidden border border-slate-200 flex flex-col">
            <div className="p-8 text-center bg-gradient-to-b from-slate-50 to-white">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-full text-blue-700 text-sm font-medium mb-6">
                <Zap className="h-4 w-4" />
                <span>Free Forever</span>
              </div>
              <div className="flex items-baseline justify-center gap-1 mb-2">
                <span className="text-5xl sm:text-6xl font-heading font-bold text-slate-900">$0</span>
                <span className="text-xl text-slate-500">/month</span>
              </div>
              <p className="text-slate-500">No credit card needed</p>
            </div>
            <div className="p-8 flex-1 flex flex-col">
              <ul className="space-y-3 mb-6 flex-1">
                {freeFeatures.map((feature, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-sage-100 flex items-center justify-center flex-shrink-0">
                      <Check className="h-3 w-3 text-sage-600" />
                    </div>
                    <span className="text-slate-700 text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
              <div className="bg-slate-50 rounded-xl px-4 py-3 mb-6 text-sm text-slate-600">
                Most users start here and upgrade after their first week when they want more sessions.
              </div>
              <button
                onClick={onStartFree}
                className="group w-full inline-flex items-center justify-center px-8 py-4 bg-slate-900 text-white font-semibold rounded-full hover:bg-slate-800 transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                Start for free
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" />
              </button>
            </div>
          </div>

          {/* Premium Plan */}
          <div className="relative">
            <div className="absolute -inset-1 bg-gradient-to-r from-coral-200 to-sage-200 rounded-[2rem] blur-xl opacity-50" />
            <div className="relative bg-white rounded-3xl shadow-xl overflow-hidden border-2 border-coral-300 flex flex-col h-full">
              <div className="absolute top-0 right-0 bg-coral-500 text-white px-4 py-1 text-xs font-bold rounded-bl-lg">
                MOST POPULAR
              </div>
              <div className="p-8 text-center bg-gradient-to-b from-coral-50 to-white">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-coral-100 rounded-full text-coral-700 text-sm font-medium mb-6">
                  <Zap className="h-4 w-4" />
                  <span>Premium</span>
                </div>
                <div className="flex items-baseline justify-center gap-1 mb-2">
                  <span className="text-5xl sm:text-6xl font-heading font-bold text-slate-900">$9</span>
                  <span className="text-xl text-slate-500">/month</span>
                </div>
                <p className="text-slate-500">Unlimited everything</p>
              </div>
              <div className="p-8 flex-1 flex flex-col">
                <ul className="space-y-3 mb-6 flex-1">
                  {premiumFeatures.map((feature, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <div className="w-5 h-5 rounded-full bg-coral-100 flex items-center justify-center flex-shrink-0">
                        <Check className="h-3 w-3 text-coral-600" />
                      </div>
                      <span className="text-slate-700 text-sm font-medium">{feature}</span>
                    </li>
                  ))}
                </ul>
                <div className="bg-coral-50 rounded-xl px-4 py-3 mb-6 text-sm text-coral-800 border border-coral-100">
                  Upgrade when you hit the free limit — your data and progress carry over automatically.
                </div>
                <button
                  onClick={onStartFree}
                  className="group w-full inline-flex items-center justify-center px-8 py-4 bg-coral-500 text-white font-semibold rounded-full hover:bg-coral-600 transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  Try it free, upgrade later
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" />
                </button>
                <div className="flex items-center justify-center gap-4 mt-4 text-sm text-slate-500">
                  <div className="flex items-center gap-1.5">
                    <Shield className="h-4 w-4" />
                    <span>Cancel anytime — no questions asked</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
